<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplateLibraryList from 'dashboard/components-next/Campaigns/Templates/TemplateLibraryList.vue';
import TemplateCreateDialog from 'dashboard/components-next/Campaigns/Templates/TemplateCreateDialog.vue';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';

const { t } = useI18n();

// Same store getter TemplateStep.vue already uses for the campaign wizard's
// inbox picker - no new inbox-listing logic.
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const inboxId = ref(null);
const templates = ref([]);
const lastSyncedAt = ref(null);
const isLoading = ref(false);
const searchQuery = ref('');
const statusFilter = ref('all');
const createDialogRef = ref(null);

const STATUS_FILTERS = [
  { value: 'all', labelKey: 'ALL' },
  { value: 'approved', labelKey: 'APPROVED' },
  { value: 'pending', labelKey: 'PENDING' },
  { value: 'rejected', labelKey: 'REJECTED' },
];

const filteredTemplates = computed(() => {
  let list = templates.value;
  if (statusFilter.value !== 'all') {
    list = list.filter(tpl => tpl.status?.toLowerCase() === statusFilter.value);
  }
  const query = searchQuery.value.trim().toLowerCase();
  if (query) {
    list = list.filter(tpl => tpl.name?.toLowerCase().includes(query));
  }
  return list;
});

const fetchTemplates = async () => {
  if (!inboxId.value) return;
  isLoading.value = true;
  try {
    const { data } = await WhatsappTemplatesAPI.get(inboxId.value);
    templates.value = data.templates || [];
    lastSyncedAt.value = data.last_synced_at;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.LIBRARY.FETCH_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const refresh = async () => {
  if (!inboxId.value) return;
  isLoading.value = true;
  try {
    const { data } = await WhatsappTemplatesAPI.sync(inboxId.value);
    templates.value = data.templates || [];
    lastSyncedAt.value = data.last_synced_at;
    useAlert(t('WHATSAPP_TEMPLATES.LIBRARY.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.LIBRARY.REFRESH_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const handleDelete = async template => {
  const message = t('WHATSAPP_TEMPLATES.LIBRARY.DELETE_CONFIRM', {
    name: template.name,
  });
  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(message);
  if (!confirmed) return;

  try {
    await WhatsappTemplatesAPI.delete(inboxId.value, template.name);
    useAlert(t('WHATSAPP_TEMPLATES.LIBRARY.DELETE_SUCCESS'));
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.LIBRARY.DELETE_ERROR')
    );
  }
};

const handleCreated = () => fetchTemplates();

watch(inboxId, fetchTemplates);
</script>

<template>
  <CampaignLayout
    :header-title="t('WHATSAPP_TEMPLATES.LIBRARY.HEADER_TITLE')"
    :button-label="t('WHATSAPP_TEMPLATES.LIBRARY.CREATE_BUTTON')"
    @click="() => createDialogRef?.open()"
  >
    <template #action>
      <TemplateCreateDialog
        v-if="inboxId"
        ref="createDialogRef"
        :inbox-id="inboxId"
        @created="handleCreated"
      />
    </template>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.LIBRARY.INBOX_LABEL') }}
        </label>
        <ComboBox
          v-model="inboxId"
          :options="inboxOptions"
          :placeholder="t('WHATSAPP_TEMPLATES.LIBRARY.INBOX_PLACEHOLDER')"
        />
      </div>

      <template v-if="inboxId">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-1">
            <Button
              v-for="filter in STATUS_FILTERS"
              :key="filter.value"
              :label="
                t(`WHATSAPP_TEMPLATES.LIBRARY.STATUS_FILTER.${filter.labelKey}`)
              "
              :variant="statusFilter === filter.value ? 'solid' : 'ghost'"
              size="sm"
              @click="statusFilter = filter.value"
            />
          </div>
          <div class="flex items-center gap-2">
            <Input
              v-model="searchQuery"
              :placeholder="t('WHATSAPP_TEMPLATES.LIBRARY.SEARCH_PLACEHOLDER')"
            />
            <Button
              :label="t('WHATSAPP_TEMPLATES.LIBRARY.REFRESH')"
              variant="outline"
              size="sm"
              icon="i-lucide-refresh-cw"
              :is-loading="isLoading"
              @click="refresh"
            />
          </div>
        </div>

        <p v-if="lastSyncedAt" class="text-xs text-n-slate-11">
          {{
            t('WHATSAPP_TEMPLATES.LIBRARY.LAST_SYNCED', {
              time: new Date(lastSyncedAt).toLocaleString(),
            })
          }}
        </p>

        <TemplateLibraryList
          :templates="filteredTemplates"
          :is-loading="isLoading"
          @delete="handleDelete"
        />
      </template>

      <p v-else class="py-10 text-sm text-center text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATES.LIBRARY.SELECT_INBOX_PROMPT') }}
      </p>
    </div>
  </CampaignLayout>
</template>
